import {
  catalogOperationsClient,
  catalogOpsClient,
  curationClient,
  experimentsClient,
  integrationsClient,
} from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError, NotImplementedError } from '~/api/errors';
import { IS_MOCK_MODE } from '~/constants/config/env';
import type {
  ArchiveBatchRequest,
  ArchiveBatchResponse,
  ArchivedFilter,
  ReconcileEnrichmentJobResponse,
  SetArchivedRequest,
} from '~/features/list-archive/types/list-archive.types';
import { runArchiveBatch } from '~/features/list-archive/utils/archive-batch';
import {
  deriveAttributeSampleSize,
  insightScoresToAttributeItems,
  insightScoresToComparison,
  insightTermsToPerformanceInsights,
  insightTrendsToComparison,
  sumOccasionTagsApplied,
  sumSearchTermsCovered,
  sumTrendsApplied,
} from '~/utils/insights-transforms';
import { INCLUDE_SOURCE_SHIPPING_PARAM } from '~/utils/source-shipping';

import {
  buildOptimizationQuality,
  toBatchCoverageDetail,
  toBatchDetail,
  toBatchHeader,
  toBatchLiveStage,
  toBatchListPage,
  toBatchRecord,
  toCatalogStats,
  toGapAnalysisProduct,
  toJobStatusCounts,
  toOnDemandScoreResult,
  toProductAttributeEdit,
  toProductEnrichment,
  toProductSearchDescriptors,
  uiTabToWireStatus,
} from '../mappers/catalog-operations.mapper';
import {
  toCurationFilterDefinitions,
  toCurationRunFilterDefinitions,
  toCurationProductDetail,
  toCurationProductsResult,
  toCurationStats,
  toProductVersionSnapshot,
  toProductVersionTimeline,
} from '../mappers/curation-products.mapper';
import type {
  BatchCoverageResponse,
  BatchDetailResponse,
  BatchInsightsResponse,
  BatchListItemWire,
  BatchListResponse,
  BatchOptimizationQualityWire,
  CatalogStatsResponse,
  ConnectorCodeWire,
  CurationFiltersResponse,
  CurationRunFiltersResponse,
  CurationInternalVersionsResponse,
  CurationProductDetailResponse,
  CurationProductListResponse,
  CurationStatsResponse,
  CurationVersionDetailResponse,
  CurationVersionTimelineResponse,
  EnrichmentJobCoverageResponse,
  EnrichmentJobHeaderResponse,
  GapAnalysisProductWire,
  InsightMembershipQuery,
  InsightMembershipResponse,
  InsightsRecomputeResponse,
  InsightsRecomputeWire,
  JobStatusCountsResponse,
  LiveStageResponse,
  OnDemandScoreResponseWire,
  PerformanceInsightsStatusResponse,
  ProductAttributeEditWire,
  ProductEnrichmentResponse,
  ProductSearchDescriptorsWire,
  ProductEnrichmentStatusRequestWire,
  ProductEnrichmentStatusResponseWire,
  ProductTagsTrendsRequestWire,
  ProductTagsTrendsScope,
  ProductTagsTrendsResponseWire,
} from '../types/catalog-operations.api.types';
import type {
  AttentionCategoryGroup,
  BatchCoverage,
  BatchCoverageDetail,
  BatchDetail,
  BatchHeader,
  BatchInsights,
  BatchListPage,
  BatchLiveStage,
  CatalogStats,
  CurationFilterDefinition,
  CurationStats,
  GapAnalysisProduct,
  JobStatusCounts,
  OnDemandScoreResult,
  OptimizationQuality,
  Product,
  ProductAttributeEdit,
  ProductDetail,
  ProductVersionSnapshot,
  ProductVersionTimeline,
  ProductFilterValues,
  ProductSearchDescriptors,
  ProductsResult,
  ProductTab,
  WorkflowFailurePointsResponse,
  WorkflowRetryMode,
  WorkflowRetryResponse,
  BatchRecord,
} from '../types/catalog-operations.types';
import type {
  LocalizedContent,
  RunTranslationSummary,
  TranslateRunResult,
} from '../types/translations.types';
import { toBatchCoverage } from '../utils/coverage-transforms';

// ---------------------------------------------------------------------------
// Live endpoints (wired to platform-gateway / catalog-ops)
// ---------------------------------------------------------------------------

/** Filters accepted by the listing service. UI-side names — the service
 *  translates `tab` and adds pagination defaults before hitting the API. */
export interface FetchBatchesParams {
  /** UI tab value: 'all' | 'in-progress' | 'delivered' | 'failed'. */
  tab?: string;
  /** Free-text search (server-side, applied across batchId/name/description). */
  search?: string;
  /** Connector / source platform filter. */
  connectorCode?: ConnectorCodeWire;
  /** Use case filter — maps to `?useCase=` on `/jobs` (e.g. 'GMC_ADS'). */
  useCase?: string;
  /** Inclusive start date filter in `YYYY-MM-DD` format. */
  fromDate?: string;
  /** Inclusive end date filter in `YYYY-MM-DD` format. */
  toDate?: string;
  /** Coverage band filter — API values: 'BELOW_90' | 'BETWEEN_90_95' | 'BETWEEN_95_100'. */
  coverage?: string;
  /** Origin filter — e.g. 'FEED_AUDIT' to isolate Feed Audit runs from the shared jobs list. */
  source?: string;
  /** List Archive visibility filter. Omit for the default list (server hides
   *  archived rows); `ARCHIVED` for the Archived view; `ALL` for existence
   *  probes that must still see archived rows. */
  archived?: ArchivedFilter;
  /** Zero-based page index. */
  page?: number;
  /** Page size; backend caps unspecified — default tuned for the table page. */
  size?: number;
}

/** Fetches the paginated batches listing. Search and pagination are applied
 *  server-side; status filter is derived from the active UI tab. */
export async function getBatches(params: FetchBatchesParams = {}): Promise<BatchListPage> {
  const status = params.tab ? uiTabToWireStatus(params.tab) : undefined;
  const searchParams: Record<string, string | number> = {
    page: params.page ?? 0,
    size: params.size ?? 20,
  };
  if (status) searchParams.status = status;
  if (params.connectorCode) searchParams.connectorCode = params.connectorCode;
  if (params.useCase) searchParams.useCase = params.useCase;
  if (params.fromDate) searchParams.fromDate = params.fromDate;
  if (params.toDate) searchParams.toDate = params.toDate;
  if (params.coverage) searchParams.coverage = params.coverage;
  if (params.source) searchParams.source = params.source;
  if (params.archived) searchParams.archived = params.archived;
  const trimmed = params.search?.trim();
  if (trimmed) searchParams.search = trimmed;

  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batches(), { searchParams })
    .json<BatchListResponse>();
  return toBatchListPage(wire);
}

/** Filters accepted by the status-counts endpoint. Mirrors `FetchBatchesParams`
 *  minus `tab`/`page`/`size` — the endpoint has no `status` param by design
 *  (it aggregates counts across every status in one grouped query). */
export type FetchJobStatusCountsParams = Pick<
  FetchBatchesParams,
  | 'search'
  | 'connectorCode'
  | 'useCase'
  | 'fromDate'
  | 'toDate'
  | 'coverage'
  | 'source'
  | 'archived'
>;

/** Fetches per-status job counts for the listing-page tab strip. Shares every
 *  non-status filter with `getBatches` so the tab badges stay in lock-step
 *  with whatever the list is currently showing. */
export async function getJobStatusCounts(
  params: FetchJobStatusCountsParams = {},
): Promise<JobStatusCounts> {
  const searchParams: Record<string, string> = {};
  if (params.connectorCode) searchParams.connectorCode = params.connectorCode;
  if (params.useCase) searchParams.useCase = params.useCase;
  if (params.fromDate) searchParams.fromDate = params.fromDate;
  if (params.toDate) searchParams.toDate = params.toDate;
  if (params.coverage) searchParams.coverage = params.coverage;
  if (params.source) searchParams.source = params.source;
  // Archived view: `status-counts?archived=ARCHIVED` — read `.all` for the
  // Archived badge (NOT `.archived`, which counts the Stop action's terminal status).
  if (params.archived) searchParams.archived = params.archived;
  const trimmed = params.search?.trim();
  if (trimmed) searchParams.search = trimmed;

  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.statusCounts(), { searchParams })
    .json<JobStatusCountsResponse>();
  return toJobStatusCounts(wire);
}

// ---------------------------------------------------------------------------
// List Archive (visibility flag)
// ---------------------------------------------------------------------------

/** Archive / unarchive toggle — `PATCH jobs/{id}/archived` `{ archived }`.
 *  Sets or clears `archivedAt` only; no workflow or status change, exactly
 *  reversible. Returns the full updated list row so callers can read the new
 *  stamp without a refetch. 409 while the run is live; unarchive is never
 *  refused. NOT the Stop action (`archiveBatch`, POST `…/archive`). */
export async function setEnrichmentJobArchived(
  jobId: string,
  archived: boolean,
): Promise<BatchRecord> {
  const body: SetArchivedRequest = { archived };
  const wire = await catalogOpsClient
    .patch(endpoints.catalogOperations.setArchived(jobId), { json: body })
    .json<BatchListItemWire>();
  return toBatchRecord(wire);
}

/** Bulk archive / unarchive — `POST jobs/archived:batch` `{ ids, archived }`,
 *  chunked at the server's 100-id cap and merged. Always 200 when well-formed;
 *  read `summary` and each `results[].outcome`, never the HTTP status. */
export async function setEnrichmentJobsArchivedBatch(
  ids: readonly string[],
  archived: boolean,
): Promise<ArchiveBatchResponse> {
  return runArchiveBatch(ids, archived, (request: ArchiveBatchRequest) =>
    catalogOpsClient
      .post(endpoints.catalogOperations.setArchivedBatch(), { json: request })
      .json<ArchiveBatchResponse>(),
  );
}

/** Non-destructive repair — `POST jobs/{id}/reconcile`. For a row that still
 *  reads IN_PROGRESS although its run finished: the server probes Temporal and
 *  stamps FAILED (never COMPLETED) when the workflow is closed. `reconciled:
 *  false` is a success — `detail` says why nothing changed. Idempotent. */
export async function reconcileEnrichmentJob(
  jobId: string,
): Promise<ReconcileEnrichmentJobResponse> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.reconcile(jobId))
    .json<ReconcileEnrichmentJobResponse>();
}

// ---------------------------------------------------------------------------
// Delivery approval signal
// ---------------------------------------------------------------------------

/** Operator decision recorded against a batch paused at AWAITING_APPROVAL. */
export type ApprovalDecision = 'APPROVED' | 'REJECTED';

export interface CatalogOperationsApprovalRequest {
  decision: ApprovalDecision;
  /** Optional operator note recorded alongside the decision. */
  notes?: string;
  /**
   * Which of the run's enriched attributes to push, chosen in the delivery panel.
   * Omitted when the operator delivers everything (or the picker was unavailable),
   * so the server keeps its default of delivering the full enriched set. Mirrors
   * the nested attribute shape the creation flow sends (`contentAttributes` +
   * `searchDescriptors`); the approval-signal endpoint reads both.
   */
  contentAttributes?: string[];
  searchDescriptors?: { enabled: boolean; phraseDescriptions: boolean };
}

/** Response from the approval-signal endpoint — `payload` echoes back whatever
 *  the workflow recorded for the decision. */
export interface CatalogOperationsApprovalResponse {
  status: string;
  payload: {
    decision: ApprovalDecision;
    notes?: string;
    zScoreDefault?: string;
  };
}

/** POST /workflow/v1/catalog-operations/{workflowId}/signal/approval —
 *  records the operator's APPROVED/REJECTED delivery decision for a run
 *  paused at AWAITING_APPROVAL. The signal envelope requires a top-level
 *  `status: "COMPLETED"` alongside the decision payload. */
export async function submitCatalogOperationsApproval(
  workflowId: string,
  payload: CatalogOperationsApprovalRequest,
): Promise<CatalogOperationsApprovalResponse> {
  return catalogOperationsClient
    .post(endpoints.catalogOperationsWorkflow.approvalSignal(workflowId), {
      json: { status: 'COMPLETED', payload },
    })
    .json<CatalogOperationsApprovalResponse>();
}

/** GET .../workflows/{workflowId}/failure-points — discovers retryable
 *  Temporal failure points for a failed batch workflow, backed by the
 *  generic `workflow-webapp` service. Used to locate the `resetToEventId`
 *  needed to retry from where the pipeline halted. */
export async function getWorkflowFailurePoints(
  workflowId: string,
): Promise<WorkflowFailurePointsResponse> {
  const res = await catalogOperationsClient
    .get(endpoints.catalogOperationsWorkflow.failurePoints(workflowId))
    .json<
      Omit<WorkflowFailurePointsResponse, 'failurePoints'> & {
        failurePoints: WorkflowFailurePointsResponse['failurePoints'] | null;
      }
    >();
  // A run past Temporal retention answers `failurePoints: null` with
  // `historyAvailable: false`; normalise so callers can always index the list.
  return { ...res, failurePoints: res.failurePoints ?? [] };
}

/** POST .../workflows/{workflowId}/retry — `mode: 'RESET'` resets the workflow
 *  to resume live from `eventId`, reusing all activity results recorded before
 *  it; `mode: 'RESUME_REMAINING'` (scheduled-pipeline `STAGE:ENRICHMENT` points
 *  only) starts a fresh run of the same `workflowId` that enriches just the
 *  missing products and continues to scoring/approval/delivery. */
export async function retryWorkflowFromStep(
  workflowId: string,
  runId: string,
  eventId: number,
  reason: string,
  mode: WorkflowRetryMode,
): Promise<WorkflowRetryResponse> {
  return catalogOperationsClient
    .post(endpoints.catalogOperationsWorkflow.retry(workflowId), {
      json: {
        runId,
        eventId,
        reason,
        mode,
        ...(mode === 'RESET' ? { reapplySignals: true } : {}),
      },
    })
    .json<WorkflowRetryResponse>();
}

/** Fetches the listing-page aggregates (stat cards + alert banner). */
export async function getCatalogStats(): Promise<CatalogStats> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.stats())
    .json<CatalogStatsResponse>();
  return toCatalogStats(wire);
}

/** Fetches full batch detail via the legacy single-call aggregate. Param is the
 *  job uuid (id from listing), not the human-readable batchId. Retained for the
 *  products page, feed-audit debug, and trial; the batch-detail page uses the
 *  phase reads below instead. */
export async function getBatchDetail(jobId: string): Promise<BatchDetail> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batchDetail(jobId))
    .json<BatchDetailResponse>();
  return toBatchDetail(wire);
}

/** Phase 1 (shell) — fetches the header slice for the batch-detail page. Fast,
 *  DB-light; the page paints its shell off this alone. Param is the job uuid. */
export async function getBatchHeader(jobId: string): Promise<BatchHeader> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batchHeader(jobId))
    .json<EnrichmentJobHeaderResponse>();
  return toBatchHeader(wire);
}

/** Phase 3 (coverage tabs) — fetches the Delivery Health + Enrichment Coverage
 *  slice. Takes the resolved `header` because a few derivations (delivery lock,
 *  enrichment duration) depend on header fields the coverage payload omits.
 *  Param is the job uuid, same id space as `getBatchHeader`. */
export async function getBatchCoverageDetail(
  jobId: string,
  header: BatchHeader,
): Promise<BatchCoverageDetail> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batchCoverageDetail(jobId))
    .json<EnrichmentJobCoverageResponse>();
  return toBatchCoverageDetail(wire, header);
}

/** Phase 4 (heaviest, lazy) — fetches the Optimization Quality slice. Only call
 *  it when the operator opens that tab. Param is the job uuid. */
export async function getBatchOptimizationQuality(jobId: string): Promise<OptimizationQuality> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batchOptimizationQuality(jobId))
    .json<BatchOptimizationQualityWire>();
  return buildOptimizationQuality(wire);
}

/** Phase 2 (live progress) — fetches the live per-stage counters. Resolves
 *  `null` on `204` (the workflow is terminal/unreachable — no live data), so the
 *  caller falls back to `header.stages` as the retrospective timeline. Param is
 *  the job uuid. */
export async function getBatchLiveStage(jobId: string): Promise<BatchLiveStage | null> {
  const res = await catalogOpsClient.get(endpoints.catalogOperations.batchLiveStage(jobId));
  // 204 is expected for terminal jobs — treat as "no live data", not an error.
  if (res.status === 204) return null;
  const wire = await res.json<LiveStageResponse>();
  return toBatchLiveStage(wire);
}

/** Reads the AI-Performance recompute state (refresh-safe). Takes the workflow
 *  slug (`BatchHeader.batchId`/`workflowId`), NOT the job uuid. Always 200 —
 *  `NONE` means never computed. Returned verbatim; the wire shape is already the
 *  domain shape. */
export async function getBatchInsightsStatus(
  workflowId: string,
): Promise<PerformanceInsightsStatusResponse> {
  return catalogOpsClient
    .get(endpoints.performanceInsights.status(workflowId))
    .json<PerformanceInsightsStatusResponse>();
}

/** Fetches the Performance Signals tab data (popular terms/attributes,
 *  occasions & trends coverage). Param is the workflow id
 *  (`BatchDetail.batchId`), not the job uuid. Transformation is delegated
 *  entirely to `~/utils/insights-transforms` — this function only assembles
 *  the pieces. */
export async function getBatchInsights(workflowId: string): Promise<BatchInsights> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batchInsights(workflowId))
    .json<BatchInsightsResponse>();
  const occasions = insightScoresToComparison(wire.PERFORMANCE_INSIGHTS.OCCASIONS_COVERAGE);
  // Trends are now split across three groups; merge them into one comparison set.
  const trendGroups = {
    top: wire.PERFORMANCE_INSIGHTS.TOP_TRENDS,
    micro: wire.PERFORMANCE_INSIGHTS.MICRO_TRENDS,
    macro: wire.PERFORMANCE_INSIGHTS.MACRO_TRENDS,
  };
  const trends = insightTrendsToComparison(trendGroups);
  const attributeSampleSize = deriveAttributeSampleSize(wire.POPULAR_ATTRIBUTES);
  const attributeCategorySizes = wire.POPULAR_ATTRIBUTE_CATEGORIES ?? [];
  return {
    popularTerms: insightTermsToPerformanceInsights(wire.PRODUCT_TERM),
    popularAttributes: insightScoresToAttributeItems(wire.POPULAR_ATTRIBUTES),
    ...(attributeCategorySizes.length > 0 ? { attributeCategorySizes } : {}),
    occasions: occasions.items,
    occasionsMax: occasions.maxValue,
    trends: trends.items,
    trendsMax: trends.maxValue,
    trendsAppliedCount: sumTrendsApplied([
      ...trendGroups.top,
      ...trendGroups.micro,
      ...trendGroups.macro,
    ]),
    searchTermsCoveredCount: sumSearchTermsCovered(wire.PRODUCT_TERM),
    occasionTagsAppliedCount: sumOccasionTagsApplied(wire.PERFORMANCE_INSIGHTS.OCCASIONS_COVERAGE),
    // Spread rather than assigned: `exactOptionalPropertyTypes` rejects an
    // explicit `undefined` on an optional field.
    ...(attributeSampleSize === undefined ? {} : { attributeSampleSize }),
  };
}

/** Fetches the products behind one Product Terms / Occasion / Popular
 *  Attribute / Macro or Micro Trend row — the drawer's data source. No
 *  transform layer: the response is already shaped for direct rendering.
 *  `label` is passed through completely unmodified. */
export async function getInsightMembershipProducts(
  workflowId: string,
  query: InsightMembershipQuery,
): Promise<InsightMembershipResponse> {
  const searchParams: Record<string, string | number> = {
    category: query.category,
    label: query.label,
  };
  if (query.productCategory) searchParams.productCategory = query.productCategory;
  if (query.group) searchParams.group = query.group;
  if (query.page !== undefined) searchParams.page = query.page;
  if (query.size !== undefined) searchParams.size = query.size;

  return catalogOpsClient
    .get(endpoints.catalogOperations.insightMembershipProducts(workflowId), { searchParams })
    .json<InsightMembershipResponse>();
}

/** Fetches the Enrichment Coverage tab's signal cards (coverage by category /
 *  attribute, attribute depth, product-detail changes). Param is the workflow
 *  id (`BatchDetail.batchId`), same as `getBatchInsights`. Transformation is
 *  delegated to `../utils/coverage-transforms` — this only issues the call. */
export async function getBatchCoverage(workflowId: string): Promise<BatchCoverage> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.batchCoverage(workflowId))
    .json<BatchCoverageResponse>();
  return toBatchCoverage(wire);
}

/**
 * Starts a Performance Signals recompute for one batch and returns the run to
 * poll. Runs on `integrationsClient` — this is integration-service, not the
 * workflow read the rest of this file talks to.
 *
 * `connectorCode` is the curation-API form (`GOOGLE_MERCHANT`, …) that
 * `BatchDetail.connectorCode` already carries — *not* the short
 * `ConnectorCodeWire` vocabulary (`GMC`) the jobs listing uses. Body is
 * camelCase.
 */
export async function startInsightsRecompute(
  workflowId: string,
  connectorCode: string,
): Promise<InsightsRecomputeWire> {
  const response = await integrationsClient
    .post(endpoints.enrichmentInsights.recompute(), { json: { workflowId, connectorCode } })
    .json<InsightsRecomputeResponse>();
  return response.data;
}

/**
 * Reads one recompute's Temporal state. Resolves `null` on 404 rather than
 * throwing: the contract makes 404 deliberately ambiguous (unknown id, aged-out
 * retention, or another subsidiary's run) so it can't confirm a run exists, and
 * on a recompute we know started it is harmless — the rows are already
 * persisted, so the caller just re-reads the insights.
 */
export async function getInsightsRecomputeStatus(
  runId: string,
): Promise<InsightsRecomputeWire | null> {
  try {
    const response = await integrationsClient
      .get(endpoints.enrichmentInsights.recomputeStatus(runId))
      .json<InsightsRecomputeResponse>();
    return response.data;
  } catch (error) {
    if (error instanceof ApiError && (error.code === 'NOT_FOUND' || error.statusCode === 404)) {
      return null;
    }
    throw error;
  }
}

/**
 * Starts a coverage-card recompute on integration-service. Twin of
 * `startInsightsRecompute` and shares its run envelope; only the resource
 * differs. `connectorCode` is the long form (`GOOGLE_MERCHANT`) that
 * `BatchDetail.connectorCode` carries, not the listing's `GMC`.
 *
 * Note the asymmetry with the rest of coverage: the cards are *read* from
 * workflow-service, but only integration-service can *produce* them.
 */
export async function startCoverageRecompute(
  workflowId: string,
  connectorCode: string,
): Promise<InsightsRecomputeWire> {
  const response = await integrationsClient
    .post(endpoints.enrichmentCoverage.recompute(), { json: { workflowId, connectorCode } })
    .json<InsightsRecomputeResponse>();
  return response.data;
}

/**
 * Reads one coverage recompute's Temporal state. Resolves `null` on 404 for the
 * same reason as the insights twin: the contract makes 404 ambiguous by design,
 * and on a run we know started it is harmless — the rows are already persisted,
 * so the caller just re-reads the coverage.
 */
export async function getCoverageRecomputeStatus(
  runId: string,
): Promise<InsightsRecomputeWire | null> {
  try {
    const response = await integrationsClient
      .get(endpoints.enrichmentCoverage.recomputeStatus(runId))
      .json<InsightsRecomputeResponse>();
    return response.data;
  } catch (error) {
    if (error instanceof ApiError && (error.code === 'NOT_FOUND' || error.statusCode === 404)) {
      return null;
    }
    throw error;
  }
}

/** Filters accepted by the curation products listing. The curation API uses
 *  1-based pagination (unlike the jobs listing which is 0-based). */
export interface FetchCurationProductsParams {
  curationRefId: string;
  /** 1-based page index — passed through to the API as `page`. */
  page?: number;
  /** Page size — passed through to the API as `size`. */
  size?: number;
  /** UI batch identifier threaded into each product so the detail link can
   *  be built without re-querying the batch. */
  batchId: string;
  /** Filter values keyed by the backend filter key (e.g. `brand`, `title`,
   *  `needs_attention`). Empty arrays / blank strings are skipped — the API
   *  expects keys to be omitted entirely when unset. */
  filters?: ProductFilterValues;
  /** Free-text search submitted by the user (Enter to commit). Serialised
   *  as the `search` query param; omitted when blank. */
  search?: string;
  /** Sort column: `title` | `sku` | `brand` | `category`. Omitted → backend
   *  default (`created_at`). Serialised as `sort_by`. */
  sortBy?: string | null;
  /** Sort direction — serialised as `sort_dir`. Defaults to `asc`. */
  sortDir?: 'asc' | 'desc';
}

/** Serialises the filter values map into the query-string shape the API
 *  expects. Empty selections are dropped so they don't degrade the query. */
function buildFilterSearchParams(filters: ProductFilterValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // multi_select — comma-joined; Ky's searchParams handles URL-encoding.
      out[key] = value.join(',');
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length === 0) continue;
      out[key] = trimmed;
    } else if (key === 'needs_attention') {
      // needs_attention is tab-driven and must always be sent explicitly
      // (true/false); other booleans only emit when true.
      out[key] = value ? 'true' : 'false';
    } else {
      // boolean — only emit when true; false means "no filter applied".
      if (value) out[key] = 'true';
    }
  }
  return out;
}

/** Fetches a paginated page of products belonging to a curation run.
 *  `filters` carries the backend filter map (brand, title, needs_attention,
 *  …); each non-empty value is serialised as a query-string param. `search`
 *  rides as its own top-level `search` param so it doesn't collide with the
 *  filter cache key. */
export async function getCurationProducts(
  params: FetchCurationProductsParams,
): Promise<ProductsResult> {
  const searchParams: Record<string, string | number> = {
    page: params.page ?? 1,
    size: params.size ?? 20,
    ...(params.filters ? buildFilterSearchParams(params.filters) : {}),
  };
  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) searchParams.search = trimmedSearch;
  // Sort — only sent when a column is selected; the backend defaults to
  // created_at otherwise (see agentic-curation-service run-products endpoint).
  if (params.sortBy) {
    searchParams.sort_by = params.sortBy;
    searchParams.sort_dir = params.sortDir ?? 'asc';
  }

  const wire = await curationClient
    .get(endpoints.curation.runProducts(params.curationRefId), { searchParams })
    .json<CurationProductListResponse>();
  return toCurationProductsResult(wire, params.batchId);
}

/** Fetches the filter definitions available for a connector (or the overlap
 *  set when `connectorCode` is omitted). The drawer renders one control per
 *  definition; option lists are pre-populated server-side. */
export async function getCurationFilters(
  connectorCode?: string,
): Promise<CurationFilterDefinition[]> {
  const wire = await curationClient
    .get(endpoints.curation.filters(), {
      searchParams: connectorCode ? { connector_code: connectorCode } : undefined,
    })
    .json<CurationFiltersResponse>();
  return toCurationFilterDefinitions(wire.data);
}

/** Fetches the filter definitions discovered from one curation run's products:
 *  only fields that carry a value in the run, with options and bounds taken
 *  from those products. `connectorCode` must be the run's connector. */
export async function getCurationRunFilters(
  curationRefId: string,
  connectorCode: string,
): Promise<CurationFilterDefinition[]> {
  const wire = await curationClient
    .get(endpoints.curation.runFilters(curationRefId), {
      searchParams: { connector_code: connectorCode },
    })
    .json<CurationRunFiltersResponse>();
  return toCurationRunFilterDefinitions(wire.data);
}

/** Filters accepted by the global needs-attention listing. The endpoint is
 *  cross-batch — products are scoped to the active subsidiary + optional
 *  connector. Omitting `connectorCode` puts the endpoint in cross-connector
 *  mode where only overlap filters (title, source_product_id, …) apply. */
export interface FetchNeedsAttentionProductsParams {
  /** Optional connector code (e.g. `GOOGLE_MERCHANT`). Omit for true
   *  cross-connector mode (overlap-filter set only). */
  connectorCode?: string;
  /** 1-based page index. */
  page?: number;
  /** Page size. */
  size?: number;
  /** Filter values keyed by the backend filter key. Same shape as the
   *  per-batch listing — `needs_attention` is implicit so callers should not
   *  pass it. */
  filters?: ProductFilterValues;
  /** Free-text search submitted by the user (Enter to commit). */
  search?: string;
}

/** Fetches subsidiary-scoped catalog aggregates (total / valid /
 *  needs_attention) with a per-connector breakdown. Drives the alert banner
 *  on the catalog operations page. Subsidiary travels via the
 *  `x-subsidiary-id` header (auto-injected by the curation client). */
export async function getCurationStats(): Promise<CurationStats> {
  const wire = await curationClient.get(endpoints.curation.stats()).json<CurationStatsResponse>();
  return toCurationStats(wire.data);
}

/** Fetches the cross-batch list of products flagged as needing attention.
 *  The row payload mirrors the curation listing shape so the existing
 *  `toCurationProduct` mapper produces the UI `Product` type unchanged.
 *  `connectorCode` is optional — omit it for cross-connector mode (only the
 *  overlap filter set applies). */
export async function getNeedsAttentionCurationProducts(
  params: FetchNeedsAttentionProductsParams,
): Promise<ProductsResult> {
  const searchParams: Record<string, string | number> = {
    page: params.page ?? 1,
    size: params.size ?? 20,
    ...(params.filters ? buildFilterSearchParams(params.filters) : {}),
  };
  if (params.connectorCode) searchParams.connector_code = params.connectorCode;
  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) searchParams.search = trimmedSearch;

  const wire = await curationClient
    .get(endpoints.curation.needsAttention(), { searchParams })
    .json<CurationProductListResponse>();
  // No batch context for these rows — pass empty string; navigation uses the
  // `/products-need-attention/{productId}` flat URL instead of `/{batchId}/...`.
  return toCurationProductsResult(wire, '');
}

// ---------------------------------------------------------------------------
// Deferred endpoints (still mocked — wire in next PR)
// ---------------------------------------------------------------------------

/** Returns the overview half of product detail. The enrichment-score and
 *  optimizations sections come from a separate API and are mapped to
 *  `undefined` here; the page conditionally hides those sections.
 *  `_batchId` is preserved for the existing hook signature so the breadcrumb
 *  can keep referencing the source batch without an extra fetch. */
export async function getProductDetail(
  _batchId: string,
  productId: string,
): Promise<ProductDetail> {
  const wire = await curationClient
    .get(endpoints.curation.productDetail(productId))
    .json<CurationProductDetailResponse>();
  return toCurationProductDetail(wire.data);
}

/**
 * Version timeline for a FEED product — newest first, live row then each
 * archived version. `changed_fields` on each entry is a diff against the NEXT
 * OLDER version, computed server-side.
 *
 * `products/{id}` is the feed product despite the name; the curated product's
 * timeline is `getInternalProductVersions` below.
 */
export async function getProductVersions(feedProductId: string): Promise<ProductVersionTimeline> {
  const wire = await curationClient
    .get(endpoints.curation.productVersions(feedProductId))
    .json<CurationVersionTimelineResponse>();
  return toProductVersionTimeline(wire);
}

/**
 * One version of a feed product in full. Pass a run's `feed_product_version` to
 * render exactly what that run processed — the payload shares the detail shape,
 * so the same component renders it without a historical variant.
 */
export async function getProductVersion(
  feedProductId: string,
  version: number,
): Promise<ProductVersionSnapshot> {
  const wire = await curationClient
    .get(endpoints.curation.productVersion(feedProductId, version))
    .json<CurationVersionDetailResponse>();
  return toProductVersionSnapshot(wire);
}

/**
 * Timeline for an INTERNAL (curated) product. Advances independently of the feed
 * timeline: a feed-only change versions the feed row and leaves this untouched.
 */
export async function getInternalProductVersions(
  productId: string,
): Promise<ProductVersionTimeline> {
  const wire = await curationClient
    .get(endpoints.curation.internalProductVersions(productId))
    .json<CurationInternalVersionsResponse>();
  return toProductVersionTimeline(wire);
}

/** Returns the enrichment slice of product detail (scores + optimizations).
 *  `includeFailed` defaults to `false` so failed-field placeholders don't
 *  bleed into the comparison accordion. `connectorCode` scopes the enrichment
 *  to the source connector (e.g. `META`, `GOOGLE`). */
export async function getProductEnrichment(
  productId: string,
  includeFailed = false,
  connectorCode?: string,
  /** The batch the product is viewed from: its own row, else the latest with `inBatch: false`. */
  jobId?: string,
): Promise<
  Pick<
    ProductDetail,
    | 'enrichmentScore'
    | 'optimizations'
    | 'attributeCount'
    | 'highlightCount'
    | 'searchDescriptorCount'
    // Carried so callers can tell a live attribute override from one a later run
    // reverted — see `isLiveAttributeEdit`.
    | 'workflowId'
    | 'enrichedAt'
    | 'retailerPrompt'
    | 'sourceShipping'
    | 'inBatch'
  >
> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.productEnrichment(productId), {
      searchParams: {
        includeFailed: String(includeFailed),
        ...(connectorCode ? { connectorCode } : {}),
        ...(jobId ? { jobId } : {}),
        ...INCLUDE_SOURCE_SHIPPING_PARAM,
      },
    })
    .json<ProductEnrichmentResponse>();
  return toProductEnrichment(wire);
}

/** One enrichment run in a catalog product's history: the same slice
 *  {@link getProductEnrichment} returns, plus the run's phase and outcome. */
export type ProductEnrichmentRun = ReturnType<typeof toProductEnrichment> & {
  phase: 'SAMPLE' | 'FULL' | null;
  success: boolean;
};

/** Returns every enrichment run for one catalog product (newest first, max 50),
 *  looked up by the catalog `externalId` rather than the curation feed id. */
export async function getProductEnrichmentHistory(
  externalId: string,
  connectorCode?: string,
): Promise<ProductEnrichmentRun[]> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.productEnrichmentHistory(), {
      searchParams: { externalId, ...(connectorCode ? { connectorCode } : {}) },
    })
    .json<ProductEnrichmentResponse[]>();
  return wire.map((w) => ({
    ...toProductEnrichment(w),
    phase: w.enrichmentPhase ?? null,
    success: w.success,
  }));
}

/** Options for an on-demand score request. */
export interface ScoreProductOnDemandOptions {
  /** Force a fresh re-score of the same enriched content. Send `true` only when
   *  a score is already present — the caller decides, and when false NO body is
   *  sent at all (the contract's "score an unscored product" default). */
  overrideCache?: boolean | undefined;
  /** Scopes the read to the source connector (`META`); omit for the job's
   *  connector / `GMC` default. Sent as a query param. */
  connectorCode?: string | undefined;
}

/** Scores / re-scores one product on demand (contract §3). Resolves on the sync
 *  fast path (`200 SCORED`/`ALREADY_SCORED`) and on the slow path (`202
 *  PENDING`, poll `getProductScore` after); `409 NOT_ENRICHED`, `404`, `502`
 *  propagate as `ApiError` for the caller's `onError`. `overrideCache` is the
 *  ONLY body field, and it is omitted entirely when not re-scoring. */
export async function scoreProductOnDemand(
  workflowId: string,
  productId: string,
  { overrideCache, connectorCode }: ScoreProductOnDemandOptions = {},
): Promise<OnDemandScoreResult> {
  const wire = await catalogOpsClient
    .post(endpoints.catalogOperations.productScore(workflowId, productId), {
      // Body only when re-scoring an existing score; an unscored product is
      // scored with no payload at all.
      ...(overrideCache ? { json: { overrideCache: true } } : {}),
      ...(connectorCode ? { searchParams: { connectorCode } } : {}),
    })
    .json<OnDemandScoreResponseWire>();
  return toOnDemandScoreResult(wire);
}

/** Reads back the currently-persisted PRE/POST for polling (contract §5). A
 *  `404` after a `202` means the on-demand run isn't visible yet (or nothing was
 *  ever requested), so it resolves to `null` — the caller keeps polling a few
 *  cycles rather than treating it as an error. Other failures propagate. */
export async function getProductScore(
  workflowId: string,
  productId: string,
  connectorCode?: string,
): Promise<OnDemandScoreResult | null> {
  try {
    const wire = await catalogOpsClient
      .get(endpoints.catalogOperations.productScore(workflowId, productId), {
        searchParams: connectorCode ? { connectorCode } : {},
      })
      .json<OnDemandScoreResponseWire>();
    return toOnDemandScoreResult(wire);
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) return null;
    throw error;
  }
}

/** Body sent when an operator overrides one enriched attribute's value. */
export interface UpdateProductAttributeRequest {
  /** Optimization wire field name, e.g. `product_title`. */
  field: string;
  /** The operator's replacement, as JSON in the attribute's own shape: an object
   *  for keyed attributes, an array for list-valued ones, a string for prose.
   *  Never stringified JSON — see `toAttributePayloadValue`. */
  value: unknown;
  /** Scopes the write to the connector the product was enriched under. */
  connectorCode?: string | undefined;
}

/** Echo of the persisted attribute. */
export interface UpdateProductAttributeResponse {
  field: string;
  value: unknown;
}

/** PATCHes a single enriched attribute with an operator-supplied value.
 *
 *  `value` travels as real JSON in the attribute's own shape; the service coerces
 *  it to the field's declared catalog type, so a list-valued field is accepted
 *  either as an array or as the bare string the read model produced for a
 *  single-item row. The 200 body echoes the value AS PERSISTED, which can differ
 *  from what was sent when a coercion applied.
 *
 *  Errors propagate untouched so the caller's `onError` can surface the real
 *  failure instead of a synthesised success: 400 for an unknown/non-editable
 *  field or a wrong-typed value, 404 for an unknown product or one outside the
 *  caller's tenant, 422 when a value fails a content rule. */
export async function updateProductAttribute(
  productId: string,
  { field, value, connectorCode }: UpdateProductAttributeRequest,
): Promise<UpdateProductAttributeResponse> {
  return catalogOpsClient
    .patch(endpoints.catalogOperations.productAttribute(productId, field), {
      json: { field, value },
      searchParams: connectorCode ? { connectorCode } : {},
    })
    .json<UpdateProductAttributeResponse>();
}

/** Fetches every operator override recorded against a product, newest-first.
 *
 *  Provenance only — the value the product currently carries comes from
 *  `getProductEnrichment`. There is no error branch to build: the endpoint never
 *  404s, answering `[]` for both an unknown product and one with no edits. */
export async function getProductAttributeEdits(
  productId: string,
  connectorCode?: string,
): Promise<ProductAttributeEdit[]> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.productAttributeEdits(productId), {
      searchParams: connectorCode ? { connectorCode } : {},
    })
    .json<ProductAttributeEditWire[]>();
  return wire.map(toProductAttributeEdit);
}

/** Fetches the content-delta gap analysis for a single product. Read-through: the
 *  first call for a product with no persisted data returns a `PENDING` envelope and
 *  starts the agent off-thread, so callers poll until `status` settles. Takes no
 *  params — the connector and run are resolved server-side from the product's
 *  latest *successful* enrichment row. */
export async function getProductGapAnalysis(productId: string): Promise<GapAnalysisProduct> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.productGapAnalysis(productId))
    .json<GapAnalysisProductWire>();
  return toGapAnalysisProduct(wire);
}

/** Forces a recompute for one product. Returns the `PENDING` envelope the backend
 *  answers 202 with; the caller then polls `getProductGapAnalysis` as it would for
 *  a first-time read. */
export async function refreshProductGapAnalysis(productId: string): Promise<GapAnalysisProduct> {
  const wire = await catalogOpsClient
    .post(endpoints.catalogOperations.productGapAnalysisRefresh(productId))
    .json<GapAnalysisProductWire>();
  return toGapAnalysisProduct(wire);
}

/** Fetches the search descriptors and phrase descriptions for a product — both
 *  strategies come back in one payload.
 *
 *  `enrichmentJobId` pins the read to one producer and should always be passed
 *  from a batch-scoped page. The descriptor tables are keyed by producer, so a
 *  product enriched by several jobs has several sets and the unpinned read
 *  resolves whichever ran last — not necessarily the batch on screen.
 *
 *  A 404 resolves to `null` rather than throwing. The contract documents 404 as
 *  "nothing generated for this product", which is a state to render, not a
 *  failure: letting it reach the query's error branch would show "could not be
 *  loaded" for a product that simply never ran either step. Every other status
 *  still rejects, so a real outage stays distinguishable from an empty result. */
export async function getProductSearchDescriptors(
  productId: string,
  enrichmentJobId?: string,
): Promise<ProductSearchDescriptors | null> {
  try {
    const wire = await catalogOpsClient
      .get(endpoints.catalogOperations.productSearchDescriptors(productId), {
        // Pins the read to one producer. Omitted only when the caller has no job
        // context, in which case the backend derives the product's latest run —
        // and that read is the Redis-cached path.
        searchParams: enrichmentJobId ? { enrichmentJobId } : {},
      })
      .json<ProductSearchDescriptorsWire>();
    return toProductSearchDescriptors(wire);
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/** Fetches per-product tags and trends for a page of products within an
 *  enrichment job or experiment. Returns a Map keyed by productId for O(1)
 *  lookup when merging into the products list. */
export async function getProductTagsTrends(
  scope: ProductTagsTrendsScope,
  productIds: string[],
): Promise<Map<string, { tags: string[]; trends: string[] }>> {
  const body: ProductTagsTrendsRequestWire = { ...scope, productIds };
  const wire = await experimentsClient
    .post(endpoints.scheduledEnrichment.productTagsTrends(), { json: body })
    .json<ProductTagsTrendsResponseWire>();
  const map = new Map<string, { tags: string[]; trends: string[] }>();
  for (const item of wire.products) {
    map.set(item.productId, { tags: item.tags, trends: item.trends });
  }
  return map;
}

/** Fetches per-product enriched/not-enriched flags for a page of products within
 *  an enrichment job. Returns a Map keyed by productId — a missing key means
 *  "not reported", which the UI must treat differently from an explicit `false`,
 *  so the badge never appears on a partial response. */
export async function getProductEnrichmentStatus(
  enrichmentJobId: string,
  productIds: string[],
): Promise<Map<string, boolean>> {
  const body: ProductEnrichmentStatusRequestWire = { enrichmentJobId, productIds };
  const wire = await experimentsClient
    .post(endpoints.scheduledEnrichment.productEnrichmentStatus(), { json: body })
    .json<ProductEnrichmentStatusResponseWire>();
  const map = new Map<string, boolean>();
  for (const item of wire.products) {
    map.set(item.productId, item.enriched);
  }
  return map;
}

/**
 * Returns the grouped list of attention items shown in the catalog Needs Attention drawer.
 * GET /catalog-operations/needs-attention — endpoint not yet wired; mock-only behind
 * IS_MOCK_MODE so production never renders fabricated alerts.
 */
export async function getNeedsAttentionGroups(): Promise<AttentionCategoryGroup[]> {
  if (!IS_MOCK_MODE) throw new NotImplementedError('/catalog-operations/needs-attention');
  return Promise.resolve(MOCK_NEEDS_ATTENTION_GROUPS);
}

const MOCK_NEEDS_ATTENTION_GROUPS: AttentionCategoryGroup[] = [
  {
    id: 'womens-footwear',
    title: "Women's Footwear — Title + Description",
    alerts: [
      {
        id: 'oos-skew',
        title: 'Out of Stock Skew Detected',
        severity: 'alert',
        description:
          'Test arm OOS rate (20.6%) exceeds Control (8.2%) by 12.4 percentage points over a 3-day rolling window. Performance data may be contaminated — impressions on OOS products are inflating Test arm spend without conversion opportunity.',
        panel: {
          title: 'Out-of-Stock Skew',
          severity: 'alert',
          metrics: [
            { label: 'Control OOS', value: '8.2%' },
            { label: 'Test OOS', value: '20.6%', tone: 'negative' },
            { label: 'Delta', value: '+12.4%', tone: 'negative' },
          ],
        },
      },
      {
        id: 'disapproval-spike',
        title: 'Disapproval Spike (Resolved)',
        severity: 'warning',
        description:
          "12 Test SKUs were flagged by Google for GTIN policy violations after the supplemental feed was pushed. The generated titles introduced brand + GTIN combinations that triggered Google's editorial check. These products are no longer serving ads in the Test arm.",
        panel: {
          title: 'Disapproval Rate',
          severity: 'warning',
          metrics: [
            { label: 'Control', value: '1.2%' },
            { label: 'Test', value: '2.8%', tone: 'negative' },
            { label: 'Delta', value: '+1.6%', tone: 'negative' },
          ],
        },
      },
    ],
  },
  {
    id: 'handbags',
    title: 'Handbags — AI Title Optimization',
    alerts: [
      {
        id: 'material-naming',
        title: 'Material Naming Convention Mismatch',
        severity: 'alert',
        description:
          '340 products in the Coach handbags catalog use internal material codes (e.g., "pbl-lthr", "xgrain-calf") that don\'t map to standard consumer-facing terms. The system generated titles using its best interpretation ("pebbled leather", "crossgrain leather") but the AI Judge flagged inconsistent naming across the category — some products use the interpreted terms while others use raw codes in the source.',
        panel: {
          title: 'Mismatch Rate',
          severity: 'warning',
          metrics: [
            { label: 'Affected Products', value: '340/1,820' },
            { label: 'Root Cause', value: 'Client Data Gap' },
            { label: 'Confidence', value: '72%' },
          ],
        },
      },
      {
        id: 'highlight-coverage',
        title: 'Product Highlight Coverage Gap',
        severity: 'info',
        description:
          '180 basic wallet and small leather goods SKUs lack sufficient differentiating attributes to generate meaningful product_highlight values. These products have SKU-level variation (color, size) but no distinguishing selling points in the source data — construction details, material stories, or exclusive features.',
        panel: {
          title: 'Coverage Gap',
          severity: 'info',
          metrics: [
            { label: 'Highlight Coverage', value: '1,640/1,820' },
            { label: 'Coverage Rate', value: '90.1%' },
            { label: 'Target', value: '≥ 95%', tone: 'neutral' },
          ],
        },
      },
    ],
  },
  {
    id: 'holiday-dresses',
    title: 'Holiday Dresses — Seasonal Refresh',
    alerts: [
      {
        id: 'approval-threshold',
        title: 'Approval Threshold Not Met',
        severity: 'alert',
        description:
          'After 24 hours, 8 Test arm SKUs remain in "pending" status in Google Merchant Center. The approval threshold (≥95%) has not been reached. Day 1 metrics collection is on hold until the threshold is met.',
        panel: {
          title: 'Mismatch Rate',
          severity: 'warning',
          metrics: [
            { label: 'Approval Rate', value: '93.2%' },
            { label: 'Pending SKUs', value: '8' },
            { label: 'Time Since Upload', value: '26 Hrs' },
          ],
        },
        info: {
          text: 'System is continuing to poll every 2 hours. Merchant API updates can take 30–60 minutes to reflect. No action required yet — this alert auto-resolves if threshold is met within 48 hours.',
        },
      },
    ],
  },
];

/**
 * Returns paginated products for a batch filtered by tab.
 * GET /catalog-operations/batches/:batchId/products — endpoint not yet wired;
 * mock-only behind IS_MOCK_MODE.
 */
export async function getProducts(_batchId: string, _tab: ProductTab): Promise<ProductsResult> {
  if (!IS_MOCK_MODE) {
    throw new NotImplementedError('/catalog-operations/batches/{batchId}/products');
  }
  return Promise.resolve({ products: MOCK_PRODUCTS, total: MOCK_PRODUCTS.length });
}

/**
 * Returns the cross-batch list of products flagged as needing attention.
 * GET /catalog-operations/needs-attention/products — endpoint not yet wired;
 * mock-only behind IS_MOCK_MODE.
 */
export async function getNeedsAttentionProducts(): Promise<ProductsResult> {
  if (!IS_MOCK_MODE) {
    throw new NotImplementedError('/catalog-operations/needs-attention/products');
  }
  return Promise.resolve({ products: MOCK_PRODUCTS, total: MOCK_PRODUCTS.length });
}

// ---------------------------------------------------------------------------
// Mock products — remove once API is wired
// ---------------------------------------------------------------------------

const MOCK_PRODUCTS: Product[] = [
  {
    id: '12612391',
    sourceProductId: '12612391',
    batchId: 'X29d4o',
    title: 'Coach Outlet Teri Shoulder Bag',
    tags: ['Quiet Luxury', 'Leather', 'Tote'],
    trends: ['Closure Type', 'Handle Style', 'Strap Type'],
    sku: 'CV933-IMXAQ',
    brand: 'COACH Outlet',
    category: 'Shoulder Bag',
  },
  {
    id: 'PD87654321',
    sourceProductId: 'PD87654321',
    batchId: 'X29d4o',
    title: 'EcoKitchen Woven Storage Basket',
    tags: ['Cottagecore', 'Eco-Friendly'],
    trends: ['Sustainability', 'Material Type'],
    sku: 'XJ482-PLQZK',
    brand: 'EcoKitchen',
    category: 'Kitchen Accessories',
  },
  {
    id: 'PD76543210',
    sourceProductId: 'PD76543210',
    batchId: 'X29d4o',
    title: 'Urban Style Running Sneakers',
    tags: ['Athleisure', 'Sustainability'],
    trends: ['Fabric Weight', 'Sole Type'],
    sku: 'YH739-TRWMB',
    brand: 'Urban',
    category: 'Footwear',
  },
  {
    id: 'PD65432109',
    sourceProductId: 'PD65432109',
    batchId: 'X29d4o',
    title: 'Nature Walk Hiking Boots',
    tags: ['Gorpcore', 'Outdoor'],
    trends: ['Material Composition', 'Waterproofing'],
    sku: 'ZK184-NVQRP',
    brand: 'NatureTrail',
    category: 'Footwear',
  },
  {
    id: 'PD54321098',
    sourceProductId: 'PD54321098',
    batchId: 'X29d4o',
    title: 'Classic Comfort Loafers',
    tags: ['Quiet Luxury', 'Minimalist', 'Office'],
    trends: ['Fit Type', 'Sole Material', 'Lining'],
    sku: 'QW567-DFJST',
    brand: 'ClassicStep',
    category: 'Footwear',
  },
  {
    id: 'PD43210987',
    sourceProductId: 'PD43210987',
    batchId: 'X29d4o',
    title: 'Sporty Casual Slip-Ons',
    tags: ['Athleisure', 'Comfort'],
    trends: ['Fabric Weight', 'Breathability'],
    sku: 'LM890-BXKCV',
    brand: 'Zenfit',
    category: 'Footwear',
  },
  {
    id: 'PD32109876',
    sourceProductId: 'PD32109876',
    batchId: 'X29d4o',
    title: 'Trendy High-Top Sneakers',
    tags: ['Techwear', 'Y2K'],
    trends: ['Material Composition', 'Colorway'],
    sku: 'OP123-MNQXZ',
    brand: 'Zenfit',
    category: 'Footwear',
  },
  {
    id: 'PD21098765',
    sourceProductId: 'PD21098765',
    batchId: 'X29d4o',
    title: 'Elegant Evening Sandals',
    tags: ['Quiet Luxury', 'Formal'],
    trends: ['Closure Type', 'Heel Height'],
    sku: 'RT456-JKZYW',
    brand: 'ElegantStep',
    category: 'Footwear',
  },
  {
    id: 'PD10987654',
    sourceProductId: 'PD10987654',
    batchId: 'X29d4o',
    title: 'Adventure Trail Shoes',
    tags: ['Gorpcore', 'Outdoor'],
    trends: ['Hem Length', 'Sole Type'],
    sku: 'UV789-PLQMN',
    brand: 'Zenfit',
    category: 'Footwear',
  },
  {
    id: 'PD09876543',
    sourceProductId: 'PD09876543',
    batchId: 'X29d4o',
    title: 'Chic Office Flats',
    tags: ['Quiet Luxury', 'Minimalist', 'Work'],
    trends: ['Occasion', 'Toe Shape', 'Lining'],
    sku: 'AB234-CDFGH',
    brand: 'ChicWalk',
    category: 'Footwear',
  },
  {
    id: 'PD11223344',
    sourceProductId: 'PD11223344',
    batchId: 'X29d4o',
    title: 'Floral Wrap Midi Dress',
    tags: ['Bloomcore', 'Feminine', 'Spring'],
    trends: ['Print Type', 'Hem Length', 'Neckline'],
    sku: 'FD901-RSTUY',
    brand: 'BloomWear',
    category: 'Dresses',
  },
  {
    id: 'PD22334455',
    sourceProductId: 'PD22334455',
    batchId: 'X29d4o',
    title: 'Oversized Linen Blazer',
    tags: ['Quiet Luxury', 'Workwear', 'Neutral'],
    trends: ['Fabric Type', 'Silhouette', 'Lapel Style'],
    sku: 'BL342-VWXYZ',
    brand: 'LuxeLayer',
    category: 'Outerwear',
  },
  {
    id: 'PD33445566',
    sourceProductId: 'PD33445566',
    batchId: 'X29d4o',
    title: 'Ribbed Knit Turtleneck',
    tags: ['Normcore', 'Minimalist'],
    trends: ['Texture', 'Fit Type', 'Neckline'],
    sku: 'KN215-ABCDE',
    brand: 'KnitCo',
    category: 'Tops & Blouses',
  },
  {
    id: 'PD44556677',
    sourceProductId: 'PD44556677',
    batchId: 'X29d4o',
    title: 'Wide-Leg Linen Trousers',
    tags: ['Quiet Luxury', 'Resort', 'Comfort'],
    trends: ['Silhouette', 'Waist Rise', 'Hem Length'],
    sku: 'TR678-FGHIJ',
    brand: 'LuxeLayer',
    category: 'Pants & Bottoms',
  },
  {
    id: 'PD55667788',
    sourceProductId: 'PD55667788',
    batchId: 'X29d4o',
    title: 'Crochet Beach Cover-Up',
    tags: ['Cottagecore', 'Bohemian', 'Summer'],
    trends: ['Fabric Type', 'Occasion', 'Pattern'],
    sku: 'CV112-KLMNO',
    brand: 'BeachDays',
    category: 'Swimwear',
  },
  {
    id: 'PD66778899',
    sourceProductId: 'PD66778899',
    batchId: 'X29d4o',
    title: 'High-Waist Yoga Leggings',
    tags: ['Athleisure', 'Performance'],
    trends: ['Compression Level', 'Waist Rise', 'Fabric Weight'],
    sku: 'YL445-PQRST',
    brand: 'Zenfit',
    category: 'Activewear',
  },
  {
    id: 'PD77889900',
    sourceProductId: 'PD77889900',
    batchId: 'X29d4o',
    title: 'Quilted Puffer Vest',
    tags: ['Gorpcore', 'Layering', 'Winter'],
    trends: ['Insulation Type', 'Fill Power', 'Silhouette'],
    sku: 'PV778-UVWXA',
    brand: 'NatureTrail',
    category: 'Outerwear',
  },
  {
    id: 'PD88990011',
    sourceProductId: 'PD88990011',
    batchId: 'X29d4o',
    title: 'Satin Slip Skirt',
    tags: ['Y2K', 'Going-Out', 'Luxe'],
    trends: ['Fabric Sheen', 'Hem Length', 'Waist Rise'],
    sku: 'SK901-BCDYZ',
    brand: 'NightOut',
    category: 'Pants & Bottoms',
  },
  {
    id: 'PD99001122',
    sourceProductId: 'PD99001122',
    batchId: 'X29d4o',
    title: 'Structured Mini Crossbody',
    tags: ['Quiet Luxury', 'Minimalist', 'Leather'],
    trends: ['Hardware Finish', 'Strap Type', 'Closure Type'],
    sku: 'CB234-EFGHI',
    brand: 'COACH Outlet',
    category: 'Crossbody Bag',
  },
  {
    id: 'PD00112233',
    sourceProductId: 'PD00112233',
    batchId: 'X29d4o',
    title: 'Denim Barrel-Leg Jeans',
    tags: ['Normcore', 'Retro', 'Casual'],
    trends: ['Silhouette', 'Wash', 'Rise'],
    sku: 'DJ567-JKLMN',
    brand: 'DenimCo',
    category: 'Pants & Bottoms',
  },
  {
    id: 'PD12345001',
    sourceProductId: 'PD12345001',
    batchId: 'X29d4o',
    title: 'Cashmere Blend Crew Neck Sweater',
    tags: ['Quiet Luxury', 'Cozy', 'Neutral'],
    trends: ['Fabric Blend', 'Texture', 'Fit Type'],
    sku: 'CS890-OPQRS',
    brand: 'LuxeLayer',
    category: 'Tops & Blouses',
  },
  {
    id: 'PD12345002',
    sourceProductId: 'PD12345002',
    batchId: 'X29d4o',
    title: 'Pleated Midi Skirt',
    tags: ['Bloomcore', 'Feminine', 'Romantic'],
    trends: ['Pleat Type', 'Hem Length', 'Fabric Type'],
    sku: 'MS123-TUVWX',
    brand: 'BloomWear',
    category: 'Pants & Bottoms',
  },
  {
    id: 'PD12345003',
    sourceProductId: 'PD12345003',
    batchId: 'X29d4o',
    title: 'Longline Parka with Hood',
    tags: ['Gorpcore', 'Winter', 'Functional'],
    trends: ['Insulation Type', 'Hood Style', 'Pocket Count'],
    sku: 'PK456-YABCD',
    brand: 'NatureTrail',
    category: 'Outerwear',
  },
  {
    id: 'PD12345004',
    sourceProductId: 'PD12345004',
    batchId: 'X29d4o',
    title: 'Tie-Dye Oversized Tee',
    tags: ['Y2K', 'Casual', 'Colorful'],
    trends: ['Print Type', 'Fit Type', 'Fabric Weight'],
    sku: 'TT789-EFGHI',
    brand: 'Urban',
    category: 'Tops & Blouses',
  },
  {
    id: 'PD12345005',
    sourceProductId: 'PD12345005',
    batchId: 'X29d4o',
    title: 'Leather Belt Bag',
    tags: ['Normcore', 'Utility', 'Minimalist'],
    trends: ['Strap Style', 'Closure Type', 'Compartments'],
    sku: 'BB012-JKLMN',
    brand: 'ChicWalk',
    category: 'Belt Bag',
  },
  {
    id: 'PD12345006',
    sourceProductId: 'PD12345006',
    batchId: 'X29d4o',
    title: 'Smocked Boho Maxi Dress',
    tags: ['Cottagecore', 'Bohemian', 'Summer'],
    trends: ['Smocking Detail', 'Neckline', 'Hem Length'],
    sku: 'MD345-OPQRS',
    brand: 'BeachDays',
    category: 'Dresses',
  },
  {
    id: 'PD12345007',
    sourceProductId: 'PD12345007',
    batchId: 'X29d4o',
    title: 'Zip-Up Fleece Hoodie',
    tags: ['Athleisure', 'Comfort', 'Layering'],
    trends: ['Fabric Weight', 'Hood Style', 'Zipper Type'],
    sku: 'FH678-TUVWX',
    brand: 'Zenfit',
    category: 'Activewear',
  },
  {
    id: 'PD12345008',
    sourceProductId: 'PD12345008',
    batchId: 'X29d4o',
    title: 'Tailored Wool-Blend Coat',
    tags: ['Quiet Luxury', 'Workwear', 'Classic'],
    trends: ['Fabric Blend', 'Lapel Style', 'Silhouette'],
    sku: 'WC901-YABCD',
    brand: 'LuxeLayer',
    category: 'Outerwear',
  },
  {
    id: 'PD12345009',
    sourceProductId: 'PD12345009',
    batchId: 'X29d4o',
    title: 'Sequin Mini Skirt',
    tags: ['Going-Out', 'Y2K', 'Festive'],
    trends: ['Embellishment Type', 'Waist Rise', 'Hem Length'],
    sku: 'SQ234-EFGHI',
    brand: 'NightOut',
    category: 'Pants & Bottoms',
  },
  {
    id: 'PD12345010',
    sourceProductId: 'PD12345010',
    batchId: 'X29d4o',
    title: 'Cargo Utility Shorts',
    tags: ['Gorpcore', 'Functional', 'Casual'],
    trends: ['Pocket Style', 'Hem Length', 'Fabric Weight'],
    sku: 'CS567-JKLMN',
    brand: 'NatureTrail',
    category: 'Pants & Bottoms',
  },
];

/** Which locales a run has been translated into. `workflowId` may be the job, pipeline or
 *  sample id; `[]` when the run was never translated. */
export async function getRunTranslations(workflowId: string): Promise<RunTranslationSummary[]> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.translations(workflowId))
    .json<RunTranslationSummary[]>();
}

/** Translates a run's enriched English into more locales (Flow B). Resolves on the `202`;
 *  poll `getRunTranslations` for the rows. `400` (no locale / nothing enriched) propagates. */
export async function translateRun(
  workflowId: string,
  locales: string[],
  productIds?: string[],
): Promise<TranslateRunResult> {
  return catalogOpsClient
    .post(endpoints.catalogOperations.translations(workflowId), {
      json: { locales, ...(productIds?.length ? { productIds } : {}) },
    })
    .json<TranslateRunResult>();
}

/** One product's content in every stored locale; `[]` when it was never translated. */
export async function getProductTranslations(
  workflowId: string,
  productId: string,
): Promise<LocalizedContent[]> {
  return catalogOpsClient
    .get(endpoints.catalogOperations.productTranslations(workflowId, productId))
    .json<LocalizedContent[]>();
}
